import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import type { Location } from '@mads/shared'
import { ConfirmDialog, ErrorNote, Loading } from '@/components/Feedback'
import {
  useEmptyLocationTrash,
  useLocationTrash,
  usePurgeLocation,
  useRestoreLocation,
} from '@/lib/queries'
import { useIsAdmin } from '@/lib/session'

function formatDeletedAt(iso: string | null): string {
  if (iso === null) return '—'

  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Either confirmation the page can be showing: one location, or the lot. */
type Pending = { kind: 'purge'; location: Location } | { kind: 'empty' }

/**
 * Binned places, kept rather than destroyed (issue #9). The whole screen is
 * admin-only, like the rest of location management (spec W-05).
 *
 * The item counts are the point of the table: kit stays attached to a binned
 * location and reads as unassigned until the place comes back, so the number
 * says what a restore would put back on the shelf — and what destroying the
 * record for good would leave with nowhere to be.
 */
export function LocationTrash() {
  const isAdmin = useIsAdmin()
  const { data: locations, isPending, error } = useLocationTrash(isAdmin)

  const restore = useRestoreLocation()
  const purge = usePurgeLocation()
  const empty = useEmptyLocationTrash()

  const [confirming, setConfirming] = useState<Pending | null>(null)

  if (!isAdmin) {
    return (
      <>
        <h1 className="page-title">Trashed locations</h1>
        <p className="empty-note">
          Only an admin can manage locations. Ask the Head of Tech if something needs restoring.
        </p>
        <Link to="/locations" className="btn btn-ghost" style={{ paddingLeft: 0 }}>
          &larr; Back to locations
        </Link>
      </>
    )
  }

  if (isPending) return <Loading what="Loading trashed locations" />
  if (error !== null) return <ErrorNote error={error} />

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 'var(--space-4)',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h1 className="page-title">Trashed locations</h1>
          <p className="page-lede">
            Removed places are kept here rather than destroyed. Anything still stored in one counts
            as unassigned until the place is restored — which puts it all back at once.
          </p>
        </div>

        {locations.length > 0 && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setConfirming({ kind: 'empty' })}
          >
            Empty trash
          </button>
        )}
      </div>

      <Link to="/locations" className="btn btn-ghost" style={{ paddingLeft: 0 }}>
        &larr; Back to locations
      </Link>

      <ErrorNote error={restore.error ?? purge.error ?? empty.error} />

      {locations.length === 0 ? (
        <p className="empty-note" style={{ marginTop: 'var(--space-6)' }}>
          No locations are in the trash.
        </p>
      ) : (
        <div className="table-scroll" style={{ marginTop: 'var(--space-6)' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th className="numeric">Items</th>
                <th>Removed</th>
                <th>By</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {locations.map((location) => (
                <tr key={location.id}>
                  <td>{location.name}</td>
                  <td className="text-muted">{location.description ?? '—'}</td>
                  <td className="numeric">{location.itemCount}</td>
                  <td className="text-muted">{formatDeletedAt(location.deletedAt)}</td>
                  <td className="text-muted">{location.deletedBy ?? 'Unknown'}</td>
                  {/* The actions stay on one line; the table scrolls instead. */}
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div className="action-row" style={{ flexWrap: 'nowrap' }}>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        disabled={restore.isPending}
                        onClick={() => restore.mutate(location.id)}
                      >
                        Restore
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => setConfirming({ kind: 'purge', location })}
                      >
                        Delete for good
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirming?.kind === 'purge' && (
        <ConfirmDialog
          title="Delete this location for good?"
          body={purgeBody(confirming.location)}
          confirmLabel="Delete for good"
          busy={purge.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            purge.mutate(confirming.location.id, { onSuccess: () => setConfirming(null) })
          }}
        />
      )}

      {confirming?.kind === 'empty' && (
        <ConfirmDialog
          title="Empty the trash?"
          body={emptyBody(locations)}
          confirmLabel="Empty trash"
          busy={empty.isPending}
          onCancel={() => setConfirming(null)}
          onConfirm={() => {
            empty.mutate(undefined, { onSuccess: () => setConfirming(null) })
          }}
        />
      )}
    </>
  )
}

/**
 * Destroying a location does not destroy its kit, and the copy has to say so —
 * "delete for good" otherwise reads as though the lanterns go with the shelf.
 */
function purgeBody(location: Location): string {
  const fate =
    location.itemCount === 0
      ? ''
      : ` Its ${countOfItems(location.itemCount)} ${verb(location.itemCount)} in the inventory, without a location.`

  return `"${location.name}" will be destroyed and can no longer be restored.${fate} This cannot be undone.`
}

function emptyBody(locations: Location[]): string {
  const items = locations.reduce((total, location) => total + location.itemCount, 0)
  const places =
    locations.length === 1
      ? 'The one location in the trash'
      : `All ${locations.length} locations in the trash`
  const where = locations.length === 1 ? 'it' : 'them'
  const fate =
    items === 0
      ? ''
      : ` The ${countOfItems(items)} still stored in ${where} ${verb(items)} in the inventory, without a location.`

  return `${places} will be destroyed and can no longer be restored.${fate} This cannot be undone.`
}

function countOfItems(count: number): string {
  return `${count} item${count === 1 ? '' : 's'}`
}

/** The verb has to agree with the count too — "its 1 item stays", not "stay". */
function verb(count: number): string {
  return count === 1 ? 'stays' : 'stay'
}
